// @ts-nocheck

import React from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { textOverflow } from 'app/shared/styles/_mixins';
import { colors } from 'app/shared/styles/_colors';
import { yieldCallback } from '@zillow/yield-callback';

// Components
import Button from 'app/shared/core/Button';
import CallLinkV2 from 'app/shared/modules/hdp/CallLinkV2';
import FloorplanImage from 'app/shared/modules/hdp/FloorplanImage';
import Iframe3dTours from 'app/shared/modules/Iframe3dTours';
import LinkToggle from 'app/shared/modules/LinkToggle';
import PhotoGalleryFull from 'app/shared/modules/photo-gallery/PhotoGalleryFull';
import PopupModal from 'app/shared/modules/popups/PopupModal';
import Tour3dButton from 'app/shared/modules/hdp/Tour3dButton';
import { useModal } from 'app/shared/contexts/ModalContext/useModal';
import NewUnitBadge from 'app/shared/modules/hdp/NewUnitBadge';
import PriceDropBadge from 'app/shared/modules/hdp/PriceDropBadge';
import {
  currentPriceStyles,
  previousPriceStyles,
  priceWithDropStyles,
} from 'app/shared/modules/hdp/PriceDropBadge/PriceDropBadge.styles';
import experimentConstants from 'app/shared/constants/ExperimentConstants';

// Utils / Misc
import { listingUtils_modelBaths, listingUtils_modelBeds, listingUtils_modelSqft } from 'app/shared/utils/listingUtils';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import appUtils from 'app/shared/utils/appUtils';
import controller from './controller';
import IconContactV2 from 'images/icons/mail-accent.svg';
import IconXWhite from 'images/icons/x-white.svg';
import {
  badgeAreaStyles,
  bathSqftStyles,
  boldDetailStyles,
  contactAreaStyles,
  contactButtonStyles,
  contactIconStyles,
  dataRowStyles,
  descriptionAreaStyles,
  detailStyles,
  expandStyles,
  gridContainerStyles,
  headerLabelsRowStyles,
  headerRowStyles,
  photoAreaStyles,
  tourOrBadgeAreaStyles,
  unitAvailabilityStyles,
  unitNameStyles,
  unitPriceHeaderStyles,
  unitPriceStyles,
} from './ModelFloorplanItem.styles';

const { func, object } = PropTypes;

const TextOverflowDiv = styled.div`
  ${textOverflow};
  ${(props) => props.hasUnitName === false && `color: ${colors['$hpx-grey-600']};`};
`;
const ModelFloorplanUnitAvailability = TextOverflowDiv;
class ModelFloorplanItem extends React.Component {
  static propTypes = {
    floorplan: object,
    listing: object,
    onActionPopup: func,
    onClickOpenFdp: func,
    openModal: func,
    closeModal: func,
    screenWidth: PropTypes.string,
    ctaExperiment: object,
  };

  static defaultProps = {
    floorplan: null,
    listing: null,
    onActionPopup() {},
    onClickOpenFdp() {},
    openModal() {},
    closeModal() {},
  };

  constructor(props) {
    super(props);
    this.LAST_SCROLL_TOP = 0;
    this.state = {
      isPopupModalOpen: false,
      isIframe3dTourOpen: false,
      isTruncated: true,
    };
  }

  handleTogglePhotoModal = () => {
    if (this.state.isPopupModalOpen) {
      if (__BROWSER__) {
        this.setState({ isPopupModalOpen: false }, () => {
          window.scrollTo(0, this.LAST_SCROLL_TOP);
        });
      }
    } else {
      this.LAST_SCROLL_TOP = document.body.scrollTop || document.scrollingElement.scrollTop;
      this.setState({ isPopupModalOpen: true });
    }
  };

  yieldOpenContactModal = yieldCallback(() => {
    const { isMobile, listing, floorplan } = this.props;
    if (isMobile) {
      appUtils.saveScrollPosition();
    }

    this.props.openModal('contact', {
      listing,
      similarListings: [],
      isPopup: true,
      unitDetails: { beds: floorplan.beds, baths: floorplan.baths, modelId: floorplan.modelId },
    });
  });

  handleContactPopupModal = (e) => {
    if (e) {
      // Check if click originated from tooltip trigger or tooltip content (same pattern as ListingCardV3)
      const target = e.target as HTMLElement;
      const clickedNewUnitTooltip = target.closest('[data-testid="new-unit-badge-tooltip"]');
      const clickedPriceDropTooltip = target.closest('[data-testid="price-drop-badge-tooltip"]');
      const clickedTooltipContent = target.closest('[role="tooltip"]');

      if (clickedNewUnitTooltip || clickedPriceDropTooltip || clickedTooltipContent) {
        e.preventDefault();
        e.stopPropagation();
        return; // Don't open contact modal
      }
    }

    // Allow users to click-and-drag to select text (e.g. copying a price) without triggering the contact form
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    const { listing } = this.props;

    if (listing.active) {
      this.yieldOpenContactModal();
    }
  };

  handleViewMoreToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ isTruncated: !this.state.isTruncated });
  };

  handleClose3dTours = () => {
    this.setState({
      isIframe3dTourOpen: false,
    });
  };

  handleToursToPhotoGallery = () => {
    this.setState({
      isIframe3dTourOpen: false,
      isPopupModalOpen: true,
    });
  };

  handleToursToContact = () => {
    const { listing, floorplan } = this.props;
    this.setState({
      isIframe3dTourOpen: false,
    });
    this.props.openModal('contact', {
      listing,
      similarListings: [],
      isPopup: true,
      unitDetails: { beds: floorplan.beds, baths: floorplan.baths, modelId: floorplan.modelId },
    });
  };

  handleOpen3dTour = (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      isIframe3dTourOpen: true,
    });
  };

  handlePhotoGalleryTo3dTours = () => {
    this.setState({
      isIframe3dTourOpen: true,
      isPopupModalOpen: false,
    });
  };

  getContactButtonText = () => {
    const { ctaExperiment } = this.props;

    // Apply CTA experiment overrides
    if (ctaExperiment && !ctaExperiment.isControl) {
      if (ctaExperiment.treatment === 'variant1') {
        return 'Check availability';
      } else if (ctaExperiment.treatment === 'variant2') {
        return 'Send message';
      }
    }

    // Control: default text
    return 'Contact';
  };

  renderPhotoModal = () => {
    const { onActionPopup, floorplan, onClickOpenFdp } = this.props;
    const { thumbPhotos, modelId, photos, zillow3dTourUrl } = floorplan;

    return (
      <PopupModal
        customCloseIcon={<img src={IconXWhite} height={20} width={20} alt="" />}
        isFullScreen
        onHidePopup={this.handleTogglePhotoModal}
        paddingTop="0"
        paddingBottom="0"
      >
        <PhotoGalleryFull
          photos={controller.checkPhotos(photos)}
          thumbPhotos={controller.checkPhotos(thumbPhotos)}
          floorplanModelId={modelId}
          onClickContact={() => {
            const { listing, floorplan } = this.props;
            this.props.openModal('contact', {
              listing,
              similarListings: [],
              isPopup: true,
              unitDetails: { beds: floorplan.beds, baths: floorplan.baths, modelId },
            });
          }}
          onClickOpenFdp={onClickOpenFdp}
          has3dTour={Boolean(zillow3dTourUrl)}
          onPhotoGalleryTo3dTours={this.handlePhotoGalleryTo3dTours}
          hideHeaderPhotoCount={true}
        />
      </PopupModal>
    );
  };

  renderIframe3dTourModal = () => {
    const { floorplan } = this.props;

    return (
      <PopupModal
        customCloseIcon={<img src={IconXWhite} height={20} width={20} alt="" />}
        isFullScreen
        onHidePopup={this.handleClose3dTours}
        paddingTop="0"
        paddingBottom="0"
      >
        <Iframe3dTours
          url={floorplan.zillow3dTourUrl}
          onClickContact={this.handleToursToContact}
          onGalleryOpen={this.handleToursToPhotoGallery}
        />
      </PopupModal>
    );
  };

  renderModelFloorplanUnit = (unit, i, showBadgeColumn = false) => {
    const { listing, screenWidth } = this.props;
    let priceText = controller.getPriceSummaryDisplay({ unit });
    if (isEmpty(priceText)) {
      priceText = <CallLinkV2 listing={listing} useLongText={screenWidth === 'xl'} />;
    }

    // Handle price drop display
    const showPriceDrop = !this.props.isPriceDropControl && unit?.hasPriceDropped;
    let priceDisplay = priceText;

    if (showPriceDrop) {
      const { currentPrice, previousPrice } = controller.getPriceDropDisplay({ unit });
      priceDisplay = (
        <div className={priceWithDropStyles}>
          <div className={currentPriceStyles}>
            {currentPrice}
            <PriceDropBadge
              variant="hdp"
              showTooltip={true}
              tooltipContent={unit?.priceDrop ? `$${unit.priceDrop} price drop` : 'Price drop'}
            />
          </div>
          {previousPrice && <div className={previousPriceStyles}>{previousPrice}</div>}
        </div>
      );
    }

    return (
      <div className={dataRowStyles} key={`floor-plan-unit-${i}`}>
        <div className={badgeAreaStyles}>
          {/* Only show New Unit badge when no price drop is showing */}
          {!showPriceDrop && !this.props.isNewUnitsControl && unit?.isNewUnit && (
            <NewUnitBadge
              text="New unit"
              variant="hdp"
              showTooltip={true}
              tooltipContent={unit?.createdAgo ? `Added ${unit.createdAgo}` : 'Recently added'}
            />
          )}
        </div>
        <div className={unitPriceStyles}>{priceDisplay}</div>
        <ModelFloorplanUnitAvailability className={unitAvailabilityStyles}>
          {controller.getAvailabilityDisplay({ listing, unit, screenWidth })}
        </ModelFloorplanUnitAvailability>
        <TextOverflowDiv className={cx(unitNameStyles)} hasUnitName={Boolean(unit.name)}>
          {unit.name || ''}
        </TextOverflowDiv>
      </div>
    );
  };

  renderModelFloorplanEmptyUnit = () => {
    const { listing, floorplan, screenWidth } = this.props;
    let priceText = controller.getPriceSummaryDisplay({ floorplan });
    if (isEmpty(priceText)) {
      priceText = <CallLinkV2 listing={listing} useLongText={screenWidth === 'xl'} />;
    }

    return (
      <div className={dataRowStyles}>
        <div className={badgeAreaStyles}></div>
        <div className={unitPriceStyles}>{priceText}</div>
        {listing.waitlisted && (
          <TextOverflowDiv className={unitAvailabilityStyles}>Currently unavailable</TextOverflowDiv>
        )}
        <div></div>
      </div>
    );
  };

  render() {
    const { floorplan } = this.props;
    const { name, baths, beds, modelId, sqft, units, thumbPhotos, zillow3dTourUrl } = floorplan;
    const { isPopupModalOpen, isTruncated, isIframe3dTourOpen } = this.state;
    const unitsToShow = isTruncated ? units.slice(0, 5) : units;
    const { active } = this.props.listing;

    return (
      <div onClick={this.handleContactPopupModal}>
        {isPopupModalOpen && this.renderPhotoModal()}

        <div className={gridContainerStyles}>
          {/* Row 1: Photo + Description + Contact Button */}
          <div className={headerRowStyles}>
            <div className={photoAreaStyles}>
              <FloorplanImage
                modelId={modelId}
                name={name}
                photos={thumbPhotos}
                onClickPopupModal={this.handleTogglePhotoModal}
                shouldShowZoom
              />
            </div>

            <div className={descriptionAreaStyles}>
              <div className={boldDetailStyles}>{listingUtils_modelBeds(beds)}</div>
              <div className={detailStyles}>
                <span className={bathSqftStyles}>{listingUtils_modelBaths(baths)}</span>
                <span className={bathSqftStyles}>{listingUtils_modelSqft(sqft)}</span>
              </div>
            </div>

            {active && (
              <div className={contactAreaStyles}>
                <Button btnType="primary-outline" size="sm" className={contactButtonStyles}>
                  <img src={IconContactV2} className={contactIconStyles} alt="" />
                  {this.getContactButtonText()}
                </Button>
              </div>
            )}
          </div>

          {/* Row 2: 3D Tour + Column Headers */}
          <div className={headerLabelsRowStyles}>
            <div className={tourOrBadgeAreaStyles}>
              {zillow3dTourUrl && (
                <div onClick={this.handleOpen3dTour}>
                  <Tour3dButton zillow3dTourUrl={zillow3dTourUrl} />
                  {isIframe3dTourOpen && this.renderIframe3dTourModal()}
                </div>
              )}
            </div>
            <div className={unitPriceHeaderStyles}>
              {shouldShowTotalMonthlyPriceBadge(this.props.listing) && this.props.listing.hasTotalMonthlyPrice
                ? 'Total price / mo'
                : 'Rent / mo'}
            </div>
            <div className={unitAvailabilityStyles}>Available</div>
            <div className={unitNameStyles}>Unit</div>
          </div>

          {/* Row 3+: Unit Data */}
          {!isEmpty(units) && <>{unitsToShow.map((unit, i) => this.renderModelFloorplanUnit(unit, i, true))}</>}

          {/* Empty units case - show price data in Row 3 */}
          {isEmpty(units) && (
            <div className={dataRowStyles}>
              <div className={badgeAreaStyles}></div>
              <div className={unitPriceStyles}>
                {controller.getPriceSummaryDisplay({ floorplan }) || (
                  <CallLinkV2 listing={this.props.listing} useLongText={this.props.screenWidth === 'xl'} />
                )}
              </div>
              {this.props.listing.waitlisted && (
                <TextOverflowDiv className={unitAvailabilityStyles}>Currently unavailable</TextOverflowDiv>
              )}
              <div></div>
            </div>
          )}
        </div>

        {units.length > 5 && (
          <div className={expandStyles} onClick={this.handleViewMoreToggle}>
            <LinkToggle onClick={() => {}} linkType="accent">
              {isTruncated ? `See all ${units.length} units` : 'See only 5 units'}
            </LinkToggle>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const newUnitsExperiment = state?.analytics?.runningExperiments?.[experimentConstants.HOTPADS_WEB_NEW_UNITS] || {};
  const isNewUnitsControl = newUnitsExperiment?.isControl !== false; // Default to control if no experiment

  const priceDropExperiment = state?.analytics?.runningExperiments?.[experimentConstants.HOTPADS_WEB_PRICE_DROPS] || {};
  const isPriceDropControl = priceDropExperiment?.isControl !== false; // Default to control if no experiment

  return {
    isMobile: state.app.device.screenWidth === 'sm',
    screenWidth: state.app.device.screenWidth,
    isNewUnitsControl,
    isPriceDropControl,
  };
};

// This HOC is used to provide modal context hook to the ModelFloorplanItem component, since it's currently a class component
const ModelFloorplanItemWithModal = (props) => {
  const { openModal: openModalFromContext, closeModal } = useModal();
  const openModal = (type, props) => {
    return openModalFromContext({
      type,
      props,
    });
  };

  // A/B Test: CTA Copy Test
  const ctaExperiment = useSelector(
    (state) => state.analytics.runningExperiments[experimentConstants.HOTPADS_WEB_CTA_COPY_TEST],
  );

  return <ModelFloorplanItem {...props} openModal={openModal} closeModal={closeModal} ctaExperiment={ctaExperiment} />;
};

export default connect(mapStateToProps)(ModelFloorplanItemWithModal);
